import { useState, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import LoginForm from './components/auth/LoginForm'
import Dashboard from './components/common/Dashboard'
import ReportsList from './components/reports/ReportsList'
import RequirementsList from './components/requirements/RequirementsList'
import IssuesList from './components/issues/IssuesList'
import ServicesList from './components/services/ServicesList'
import ServicesDisplayPage from './components/services/ServicesDisplayPage'
import ClientsList from './components/clients/ClientsList'
import InfrastructureList from './components/infrastructure/InfrastructureList'
import StatisticsPage from './components/statistics/StatisticsPage'
import LiveStatisticsPage from './components/live-statistics/LiveStatisticsPage'
import SettingsPageComponent from './components/settings/SettingsPage'
import ActivityLogsPage from './components/activity-logs/ActivityLogsPage'
import MapPage from './components/map/MapPage'
import RealizationsBoard from './components/realizations/RealizationsBoard'
import SchedulePage from './components/schedule/SchedulePage'
import GlobalLayout from './components/common/GlobalLayout'
import { NotificationProvider } from './contexts/NotificationContext'

// Odczyt czasu wygaśnięcia (exp, sekundy) z JWT bez weryfikacji podpisu.
function parseJwtExp(t: string): number | null {
  try {
    const payload = JSON.parse(
      atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
    )
    return typeof payload.exp === 'number' ? payload.exp : null
  } catch {
    return null
  }
}

// Cichy refresh sesji: wymienia refresh token na nowy token dostępowy.
// Zwraca nowy token dostępowy albo null (gdy brak/wygasł refresh lub błąd sieci).
async function refreshAccessToken(): Promise<string | null> {
  const rt = localStorage.getItem('refresh_token')
  if (!rt) return null
  try {
    const res = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: rt }),
    })
    if (!res.ok) {
      // 401 = refresh nieważny -> usuń; inne (5xx) -> zostaw, spróbujemy później
      if (res.status === 401) localStorage.removeItem('refresh_token')
      return null
    }
    const data = await res.json()
    localStorage.setItem('token', data.access_token)
    if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token)
    return data.access_token
  } catch {
    return null // błąd sieci: nie kasujemy refresh tokenu
  }
}

function App() {
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check for existing token on app load
    const savedToken = localStorage.getItem('token')
    if (savedToken) {
      setToken(savedToken)
      setLoading(false)
    } else if (localStorage.getItem('refresh_token')) {
      // Brak tokenu dostępowego, ale mamy refresh -> odnów sesję po cichu
      refreshAccessToken().then((newTok) => {
        if (newTok) setToken(newTok)
        setLoading(false)
      })
    } else {
      setLoading(false)
    }

    // Listen for token updates from other tabs
    const bc = new BroadcastChannel('auth')
    bc.onmessage = (event) => {
      if (event.data.type === 'token-update') {
        setToken(event.data.token)
        localStorage.setItem('token', event.data.token)
      }
    }

    // Throttle token validity checks - max once per 5 minutes
    let lastTokenCheck = 0
    const TOKEN_CHECK_INTERVAL = 5 * 60 * 1000 // 5 minutes

    // Function to check token validity
    const checkTokenValidity = async () => {
      const now = Date.now()
      if (now - lastTokenCheck < TOKEN_CHECK_INTERVAL) {
        return // Skip if checked recently
      }
      lastTokenCheck = now

      const currentToken = localStorage.getItem('token')
      if (!currentToken) {
        setToken(null)
        return
      }

      try {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 10000) // 10s timeout

        const response = await fetch('/api/auth/me', {
          headers: {
            'Authorization': `Bearer ${currentToken}`,
          },
          signal: controller.signal,
        })

        clearTimeout(timeoutId)

        // Token wygasł/niewłaściwy -> najpierw spróbuj cicho odnowić sesję
        if (response.status === 401 || response.status === 403) {
          const newTok = await refreshAccessToken()
          if (newTok) {
            // Sesja odnowiona - zostajemy zalogowani, synchronizujemy karty
            setToken(newTok)
            bc.postMessage({ type: 'token-update', token: newTok })
          } else {
            // Brak ważnego refresh tokenu -> realne wylogowanie
            console.warn('Sesja wygasła (status ' + response.status + '), wylogowanie')
            localStorage.removeItem('token')
            localStorage.removeItem('refresh_token')
            setToken(null)
            bc.postMessage({ type: 'logout' })
          }
        }
        // For other errors (500, 502, 503, etc.) - keep the token, server may be temporarily down
      } catch (error) {
        console.error('Error checking token validity:', error)
        // On network error or timeout - keep the token, don't logout
        // This prevents logout when mobile browser loses connection temporarily
      }
    }

    // Check token when page becomes visible (user returns to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkTokenValidity()
      }
    }

    // Check token when window gets focus
    const handleFocus = () => {
      checkTokenValidity()
    }

    // Listen for storage changes (token removed in another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token' && !e.newValue) {
        setToken(null)
      }
    }

    // Listen for logout messages from other tabs
    const handleBroadcastMessage = (event: MessageEvent) => {
      if (event.data.type === 'logout') {
        setToken(null)
        localStorage.removeItem('token')
        localStorage.removeItem('refresh_token')
      }
    }

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)
    window.addEventListener('storage', handleStorageChange)
    bc.addEventListener('message', handleBroadcastMessage)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('storage', handleStorageChange)
      bc.removeEventListener('message', handleBroadcastMessage)
      bc.close()
    }
  }, [])

  // Proaktywne odnawianie: odśwież token ~60 s przed jego wygaśnięciem, żeby przy
  // krótkim czasie życia (np. 30 min) nie łapać 401 w trakcie pracy. Reharmonogram
  // następuje po każdej zmianie tokenu (nowy token -> nowy timer).
  useEffect(() => {
    if (!token) return
    if (!localStorage.getItem('refresh_token')) return
    const exp = parseJwtExp(token)
    if (!exp) return
    const msUntilRefresh = exp * 1000 - Date.now() - 60_000
    const delay = Math.min(Math.max(msUntilRefresh, 5_000), 2_147_000_000)
    const id = window.setTimeout(async () => {
      const newTok = await refreshAccessToken()
      if (newTok) {
        setToken(newTok)
        const bc = new BroadcastChannel('auth')
        bc.postMessage({ type: 'token-update', token: newTok })
        bc.close()
      }
    }, delay)
    return () => window.clearTimeout(id)
  }, [token])

  const handleLogin = (newToken: string) => {
    setToken(newToken)
  }

  const handleLogout = () => {
    const accessToken = localStorage.getItem('token')
    const refresh = localStorage.getItem('refresh_token')
    // Unieważnij refresh token po stronie serwera (best-effort)
    if (accessToken) {
      fetch('/api/auth/logout', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(refresh ? { refresh_token: refresh } : {}),
      }).catch(() => {})
    }

    setToken(null)
    localStorage.removeItem('token')
    localStorage.removeItem('refresh_token')

    // Broadcast logout to other tabs
    const bc = new BroadcastChannel('auth')
    bc.postMessage({ type: 'logout' })
    bc.close()
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Ładowanie aplikacji...</p>
        </div>
      </div>
    )
  }

  return (
    <NotificationProvider>
      <Routes>
        <Route 
          path="/login" 
          element={
            token ? <Navigate to="/dashboard" replace /> : <LoginForm onLogin={handleLogin} />
          } 
        />
        <Route 
          path="/dashboard" 
          element={
            token ? <Dashboard token={token} onLogout={handleLogout} /> : <Navigate to="/login" replace />
          } 
        />
        <Route 
          path="/clients" 
          element={
            token ? <ClientsPage token={token} onLogout={handleLogout} /> : <Navigate to="/login" replace />
          } 
        />
        <Route
          path="/reports"
          element={
            token ? <ReportsPage token={token} onLogout={handleLogout} /> : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/requirements"
          element={
            token ? <RequirementsPage token={token} onLogout={handleLogout} /> : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/issues"
          element={
            token ? <IssuesPage token={token} onLogout={handleLogout} /> : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/services"
          element={
            token ? <ServicesPage token={token} onLogout={handleLogout} /> : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/services-display"
          element={
            <ServicesDisplayPageWrapper token={token} />
          }
        />
        <Route
          path="/infrastructure"
          element={
            token ? <InfrastructurePage token={token} onLogout={handleLogout} /> : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/statistics"
          element={
            token ? <StatisticsPageWrapper token={token} onLogout={handleLogout} /> : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/live-statistics"
          element={
            token ? <LiveStatisticsPageWrapper token={token} onLogout={handleLogout} /> : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/settings"
          element={
            token ? <SettingsPage token={token} onLogout={handleLogout} /> : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/activity-logs"
          element={
            token ? <ActivityLogsPageWrapper token={token} onLogout={handleLogout} /> : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/map"
          element={
            token ? <MapPage token={token} onLogout={handleLogout} /> : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/realizations"
          element={
            token ? <RealizationsPage token={token} onLogout={handleLogout} /> : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/schedule"
          element={
            token ? <SchedulePageWrapper token={token} onLogout={handleLogout} /> : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/"
          element={
            token ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
          }
        />
        <Route 
          path="/index.html" 
          element={
            token ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
          } 
        />
        <Route 
          path="*" 
          element={
            token ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
          } 
        />
      </Routes>
    </NotificationProvider>
  )
}

// Placeholder components for other pages
function ClientsPage({ token, onLogout }: { token: string; onLogout: () => void }) {
  return (
    <GlobalLayout token={token} onLogout={onLogout}>
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <ClientsList token={token} />
        </div>
      </div>
    </GlobalLayout>
  )
}

function ReportsPage({ token, onLogout }: { token: string; onLogout: () => void }) {
  return (
    <GlobalLayout token={token} onLogout={onLogout}>
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <ReportsList token={token} />
        </div>
      </div>
    </GlobalLayout>
  )
}

function RequirementsPage({ token, onLogout }: { token: string; onLogout: () => void }) {
  return (
    <GlobalLayout token={token} onLogout={onLogout}>
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <RequirementsList token={token} />
        </div>
      </div>
    </GlobalLayout>
  )
}

function InfrastructurePage({ token, onLogout }: { token: string; onLogout: () => void }) {
  return (
    <GlobalLayout token={token} onLogout={onLogout}>
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <InfrastructureList token={token} />
        </div>
      </div>
    </GlobalLayout>
  )
}

function IssuesPage({ token, onLogout }: { token: string; onLogout: () => void }) {
  return (
    <GlobalLayout token={token} onLogout={onLogout}>
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <IssuesList token={token} />
        </div>
      </div>
    </GlobalLayout>
  )
}

function SettingsPage({ token, onLogout }: { token: string; onLogout: () => void }) {
  return (
    <GlobalLayout token={token} onLogout={onLogout}>
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <SettingsPageComponent token={token} />
        </div>
      </div>
    </GlobalLayout>
  )
}

function StatisticsPageWrapper({ token, onLogout }: { token: string; onLogout: () => void }) {
  return (
    <GlobalLayout token={token} onLogout={onLogout}>
      <StatisticsPage token={token} />
    </GlobalLayout>
  )
}

function LiveStatisticsPageWrapper({ token, onLogout }: { token: string; onLogout: () => void }) {
  return (
    <GlobalLayout token={token} onLogout={onLogout}>
      <LiveStatisticsPage token={token} />
    </GlobalLayout>
  )
}

function ActivityLogsPageWrapper({ token, onLogout }: { token: string; onLogout: () => void }) {
  return (
    <GlobalLayout token={token} onLogout={onLogout}>
      <ActivityLogsPage token={token} />
    </GlobalLayout>
  )
}

function ServicesPage({ token, onLogout }: { token: string; onLogout: () => void }) {
  return (
    <GlobalLayout token={token} onLogout={onLogout}>
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <ServicesList token={token} />
        </div>
      </div>
    </GlobalLayout>
  )
}

function ServicesDisplayPageWrapper({ token }: { token: string | null }) {
  return <ServicesDisplayPage token={token} />
}

function RealizationsPage({ token, onLogout }: { token: string; onLogout: () => void }) {
  return (
    <GlobalLayout token={token} onLogout={onLogout}>
      <div className="max-w-[100rem] mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <RealizationsBoard token={token} />
        </div>
      </div>
    </GlobalLayout>
  )
}

function SchedulePageWrapper({ token, onLogout }: { token: string; onLogout: () => void }) {
  return (
    <GlobalLayout token={token} onLogout={onLogout}>
      <SchedulePage token={token} />
    </GlobalLayout>
  )
}



export default App
